/**
 * /refund — NDORFlow Refund Policy.
 *
 * RC 1.35.F1 P0-4.
 */
import LegalPageShell from './LegalPageShell';

export default function RefundPage() {
  return (
    <LegalPageShell
      title="Refund Policy"
      updatedAt="28 April 2026"
      activePath="/refund"
      testid="refund-page"
    >
      <p>
        This Refund Policy describes when and how refunds may be available for NDORFlow purchases. It
        forms part of our <a href="/terms">Terms of Service</a>.{' '}
        <strong>IAA Energy Resources Limited</strong> (Company No. 11583381) is the seller and
        merchant of record for your NDORFlow subscription and any top-up purchase, and issues any
        refund to the original payment method. Our goal is to be fair, transparent, and to comply
        with applicable consumer-protection law in your country of residence.
      </p>

      <h2>1. Credit Purchases (Top-ups)</h2>
      <ul>
        <li>
          <strong>Unused Credits.</strong> If you have not consumed any Credits from a top-up package,
          you may request a full refund within <strong>14 days</strong> of purchase. This right mirrors
          the statutory right of withdrawal for digital services in the UK and EU, which you may waive
          by using the Credits. Once you consume any Credits from the package, the package is treated
          as partly delivered and is non-refundable except as set out below.
        </li>
        <li>
          <strong>Used Credits.</strong> Credits that have been consumed (Analyse Fit, CV generation,
          cover-letter generation, or any other paid action that was attempted and delivered) are not
          refundable. Where NDORFlow waives a charge automatically because an action did not produce an
          improvement (e.g. "Tailoring complete — no charge (no improvement)"), you are not billed and
          there is nothing to refund.
        </li>
      </ul>

      <h2>2. Subscriptions (Focus, Flow)</h2>
      <ul>
        <li>
          <strong>Cancellation.</strong> You may cancel at any time from Account &gt; Plan or via the
          Stripe Customer Portal. Cancellation stops future renewals. You retain access to the paid
          plan until the end of your current billing period.
        </li>
        <li>
          <strong>Monthly plans.</strong> Monthly fees are not pro-rated on cancellation. You keep
          access through the end of the month you have paid for.
        </li>
        <li>
          <strong>Annual plans.</strong> If you cancel within 14 days of first subscribing and have
          consumed fewer than 50% of the Included Credits allocated for that period, you may request a
          full refund. Beyond that window, annual plans are non-refundable but continue to the end of
          the paid year.
        </li>
        <li>
          <strong>Material Service failure.</strong> If a Service defect we are responsible for
          prevents you from using the plan substantially as described, and we cannot remedy the defect
          within 14 days of you reporting it, you may request a pro-rated refund for the affected
          period.
        </li>
      </ul>

      <h2>3. Billing Cycles &amp; Renewals</h2>
      <p>
        Paid plans renew automatically at the end of each billing period unless cancelled before the
        renewal date. You will receive a renewal email receipt from Stripe. If a renewal charge
        appears on your statement after you intended to cancel, contact us within 30 days of the
        charge and we will consider a refund of the renewal subject to the rules above.
      </p>

      <h2>4. Payment Disputes &amp; Chargebacks</h2>
      <p>
        If you believe a charge is incorrect, please contact us first at{' '}
        <a href="mailto:support@ndor.app">support@ndor.app</a>. We aim to acknowledge within
        2 business days and resolve within 10 business days. Filing a chargeback with your bank or
        card issuer without contacting us first may result in account suspension pending the dispute
        outcome. We will respond to all bank chargebacks with the relevant billing records.
      </p>

      <h2>5. Exceptional Scenarios</h2>
      <ul>
        <li>
          <strong>Duplicate charges.</strong> If you are charged twice for the same purchase due to a
          payment-processor error, we will refund the duplicate in full on confirmation.
        </li>
        <li>
          <strong>Unauthorised charges.</strong> If you report a charge you did not authorise within
          60 days, we will investigate and, where appropriate, refund and lock the account pending
          identity re-verification.
        </li>
        <li>
          <strong>Service outages.</strong> We may issue Credits or partial refunds at our discretion
          for extended outages that materially affect your ability to use the Service.
        </li>
      </ul>

      <h2>6. Consumer Protections</h2>
      <p>
        This policy does not affect your statutory consumer rights. In the UK, your rights under the
        Consumer Rights Act 2015 and the Consumer Contracts Regulations 2013 continue to apply. In the
        EU, your rights under the Consumer Rights Directive (2011/83/EU) continue to apply. In the
        US, applicable state consumer-protection law continues to apply. Where local law requires a
        more generous refund than this policy provides, the local law prevails for that purchase.
      </p>

      <h2>7. How to Request a Refund</h2>
      <ol>
        <li>Email <a href="mailto:support@ndor.app">support@ndor.app</a> with the subject line "Refund request — [your email]".</li>
        <li>Include the Stripe transaction ID or the date and amount of the charge.</li>
        <li>Describe the reason and any relevant screenshots.</li>
        <li>We will acknowledge within 2 business days and decide within 10 business days.</li>
        <li>Approved refunds are returned to the original payment method and usually clear within 5–10 business days depending on your bank.</li>
      </ol>

      <h2>8. Changes to this Policy</h2>
      <p>
        We may update this Refund Policy from time to time. The "Last updated" date at the top of this
        page reflects the latest version. Purchases made before a change are governed by the policy
        in force at time of purchase.
      </p>

      <h2>9. Contact</h2>
      <p>
        For refund requests, email{' '}
        <a href="mailto:support@ndor.app">support@ndor.app</a>. For general questions, see our{' '}
        <a href="/terms">Terms of Service</a> or email{' '}
        <a href="mailto:support@ndor.app">support@ndor.app</a>.
      </p>
    </LegalPageShell>
  );
}
